import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function ImageModal({ images, initialIndex, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Minimum swipe distance in pixels
  const minSwipeDistance = 50;

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "ArrowLeft") handlePrev();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentIndex]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  // Touch swipe handlers
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe) {
      handlePrev();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm">
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-50 rounded-full bg-brand-charcoal/80 p-3 text-white hover:bg-brand-accent hover:text-white transition-colors focus:outline-none"
        aria-label="Luk visning"
      >
        <X className="h-6 w-6" />
      </button>

      {/* Navigation buttons (hidden on small touch screens, but visible on hover/desktop) */}
      <button
        onClick={handlePrev}
        className="absolute left-4 z-50 hidden sm:flex items-center justify-center rounded-full bg-brand-charcoal/50 p-4 text-white hover:bg-brand-accent transition-colors focus:outline-none"
        aria-label="Forrige billede"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>

      {/* Image container */}
      <div
        className="relative flex h-full w-full max-w-5xl items-center justify-center p-4 sm:p-12 select-none"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={(e) => {
          // Close modal if clicking outside the image
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <img
          src={`https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/${images[currentIndex].replace(
            "https://assets.auxodev.dk/",
            ""
          )}`}
          alt={`Galleri billede ${currentIndex + 1}`}
          className="max-h-[85vh] max-w-full rounded border border-brand-gray-dark object-contain shadow-2xl"
          draggable="false"
        />

        {/* Image Counter */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded bg-brand-charcoal/80 px-4 py-1.5 text-xs font-semibold tracking-wider text-gray-300">
          {currentIndex + 1} / {images.length}
        </div>
      </div>

      <button
        onClick={handleNext}
        className="absolute right-4 z-50 hidden sm:flex items-center justify-center rounded-full bg-brand-charcoal/50 p-4 text-white hover:bg-brand-accent transition-colors focus:outline-none"
        aria-label="Næste billede"
      >
        <ChevronRight className="h-6 w-6" />
      </button>
    </div>
  );
}
